import React from 'react'

import { BaseView, BaseText } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'

interface RoundedProgressBarProps {
  percentage: number
  text: string
  size?: number
  strokeWidth?: number
  color?: string
  bgColor?: string
}

export const RoundedProgressBar: React.FC<RoundedProgressBarProps> = ({
  percentage,
  text,
  size = 80,
  strokeWidth = 6,
  color,
  bgColor,
}) => {
  const theme = useTheme()

  // Normalize percentage to 0-1 range (like in RN version)
  const normalizedPercentage = percentage > 1 ? percentage / 100 : percentage
  const clampedPercentage = Math.max(0, Math.min(1, normalizedPercentage))

  // Calculate stroke dash array for progress circle (like in RN version)
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - clampedPercentage * circumference

  return (
    <BaseView
      style={{
        width: `${size}px`,
        height: `${size}px`,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* SVG Progress Circle */}
      <svg
        width={size}
        height={size}
        style={{
          position: 'absolute',
          transform: 'rotate(-90deg)', // Start from top
        }}
      >
        {/* Background Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={
            bgColor ||
            theme.colors.background.roundedProgressBackground ||
            theme.colors.background.secondaryGray
          }
          strokeWidth={strokeWidth}
          fill="transparent"
        />

        {/* Progress Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color || theme.colors.background.roundedProgressFill}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={`${circumference}, ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 0.8s ease-in-out',
          }}
        />
      </svg>

      {/* Center Content - Only category text like in RN */}
      {text && (
        <BaseView
          center
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BaseText
            fontSize={28}
            fontFamily="regular"
            color="label.default"
            textAlign="center"
            style={{
              maxWidth: `${size - 16}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </BaseText>
        </BaseView>
      )}
    </BaseView>
  )
}
